import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@common-fe/common-fe';
import styled from 'styled-components';

import { Accordion } from '@/components/elements/Accordion';
import useModalType from '@/modules/plan/PlanSetup/FinalSetup/stores/useModalType.store';

import { PlanStatuses } from '../../plan.types';
import { PlanTerminationBanner } from '../../PlanTermination/PlanTerminationBanner';
import PlanUpdateModal from '../PlanUpdateModal';

import PlanTemplateActivation from './PlanTemplateActivation';
import PlanTopbarActions from './PlanTopbarActions';
import PlanTopbarEditModeButton from './PlanTopbarEditModeButton';
import TemplateUnderUpdateBanner from './TemplateUnderUpdateBanner';
import TopbarWrapper from './TopbarWrapper';

const FixedWrapper = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 12;
`;

interface Props {
  planStatus?: PlanStatuses;
  planName?: string;
  viewMode?: boolean;
  isTemplateMode?: boolean;
  isPlanActive?: boolean;
  isEditableTemplate?: boolean;
  isTemplateUnderUpdate?: boolean;
  saveButtonRef?: React.RefObject<HTMLButtonElement | null | undefined>;
  activateButtonRef?: React.RefObject<HTMLButtonElement | null | undefined>;
  reviewButtonRef?: React.RefObject<HTMLButtonElement | null | undefined>;
}
const PlanTopbar: React.FC<Props> = ({
  planStatus,
  viewMode,
  isTemplateMode,
  saveButtonRef,
  reviewButtonRef,
  activateButtonRef,
  planName,
  isPlanActive,
  isEditableTemplate,
  isTemplateUnderUpdate,
}) => {
  const [isVisibleUpdatePlanPopup, setVisibleUpdatePlanPopup] = useState(false);
  const handleToggleUpdatePlanPopup = useCallback(() => {
    setVisibleUpdatePlanPopup(!isVisibleUpdatePlanPopup);
  }, [isVisibleUpdatePlanPopup]);

  const handleActive = useCallback(() => {
    if (activateButtonRef?.current) {
      activateButtonRef?.current.click();
    }
  }, [activateButtonRef]);

  const { saveErrorModal, activateModal, activateErrorModal, isEmptySaveModal, updateErrorModal } =
    useModalType();

  const isPlanTerminated = useMemo(() => {
    return (
      planStatus === PlanStatuses.TERMINATED ||
      planStatus === PlanStatuses.TERMINATION_IN_PROGRESS ||
      planStatus === PlanStatuses.PENDING_TERMINATION
    );
  }, [planStatus]);

  useEffect(() => {
    if (
      (saveErrorModal || activateModal || activateErrorModal || isEmptySaveModal || updateErrorModal) &&
      setVisibleUpdatePlanPopup
    ) {
      setVisibleUpdatePlanPopup(false);
    }
  }, [
    saveErrorModal,
    activateModal,
    activateErrorModal,
    isEmptySaveModal,
    updateErrorModal,
    setVisibleUpdatePlanPopup,
  ]);

  const node = useMemo(() => {
    if (isTemplateMode) {
      if (
        planStatus !== PlanStatuses.ACTIVE &&
        planStatus !== PlanStatuses.PENDING_ACTIVE &&
        planStatus !== PlanStatuses.UNDER_UPDATE
      ) {
        if (viewMode) {
          return (
            <PlanTopbarEditModeButton
              isDraftMode={planStatus === PlanStatuses.DRAFT}
              isPlanTerminated={isPlanTerminated}
              isTemplateUnderUpdate={isTemplateUnderUpdate}
            />
          );
        }

        return (
          <PlanTopbarActions
            saveButtonRef={saveButtonRef}
            activateButtonRef={activateButtonRef}
            reviewButtonRef={reviewButtonRef}
            viewMode={viewMode}
            isTemplateMode={isTemplateMode}
          />
        );
      }
      // Active plan template editing (EL-33723): mirror active plan edit/save flow
      if (isEditableTemplate) {
        if (viewMode) {
          return (
            <Box direction="row" align="center" gap="spacing12">
              <PlanTopbarEditModeButton
                isPlanActive={isPlanActive}
                isTemplateMode={isTemplateMode}
                isPlanTerminated={isPlanTerminated}
                isTemplateUnderUpdate={isTemplateUnderUpdate}
              />
              <PlanTemplateActivation />
            </Box>
          );
        }
        return (
          <PlanTopbarActions
            saveButtonRef={saveButtonRef}
            activateButtonRef={activateButtonRef}
            reviewButtonRef={reviewButtonRef}
            viewMode={viewMode}
            isTemplateMode={isTemplateMode}
            planStatus={planStatus}
            isEditableTemplate={isEditableTemplate}
            handleToggleUpdatePlanPopup={handleToggleUpdatePlanPopup}
          />
        );
      }
      return <PlanTemplateActivation />;
    }

    if (viewMode) {
      return (
        <PlanTopbarEditModeButton
          isDraftMode={planStatus === PlanStatuses.DRAFT}
          isPlanActive={isPlanActive}
          isPlanTerminated={isPlanTerminated}
        />
      );
    }
    return (
      <PlanTopbarActions
        saveButtonRef={saveButtonRef}
        activateButtonRef={activateButtonRef}
        reviewButtonRef={reviewButtonRef}
        viewMode={viewMode}
        isTemplateMode={isTemplateMode}
        planStatus={planStatus}
        handleToggleUpdatePlanPopup={handleToggleUpdatePlanPopup}
      />
    );
  }, [
    activateButtonRef,
    isTemplateMode,
    planStatus,
    reviewButtonRef,
    saveButtonRef,
    viewMode,
    handleToggleUpdatePlanPopup,
    isPlanActive,
    isPlanTerminated,
    isEditableTemplate,
    isTemplateUnderUpdate,
  ]);

  return (
    <>
      <FixedWrapper
        data-testid="plan-topbar-fixed-wrapper"
        height={{ min: 'fit-content' }}
        width="100%"
        background={{ color: 'canvas' }}
      >
        <TopbarWrapper>{node}</TopbarWrapper>
        <TemplateUnderUpdateBanner isVisible={isTemplateUnderUpdate} />
        <PlanTerminationBanner />
        <Accordion />
      </FixedWrapper>
      <PlanUpdateModal
        handleActive={handleActive}
        isVisible={isVisibleUpdatePlanPopup}
        onSetVisible={handleToggleUpdatePlanPopup}
        planName={planName}
        isTemplateMode={isTemplateMode}
      />
    </>
  );
};
export default React.memo(PlanTopbar);
